import React, { useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save, Shield } from "lucide-react";
import { handleSend, handleGet } from "../../controllers/controller.all";
import SubmitButton from "../../components/ui/SubmitButton";


export default function AdminGlobalCreate() {
    const user = useSelector((state) => state.userReducer?.user);
    const token = user?.access_token || "";
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [ecoles, setEcoles] = useState([]);
    
    const [formData, setFormData] = useState({
        nom: "",
        prenom: "",
        email: "",
        tel: "",
        adresse: "",
        password: "",
        role: "ADMIN",
        ecoles: []
    });

    useEffect(() => {
        const fetchEcoles = async () => {
            if (!token) return;
            await handleGet(`/ecole`, (res) => {
                if (res?.ecoles) {
                    setEcoles(res.ecoles);
                }
            }, token);
        };
        fetchEcoles();
    }, [token]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const handleEcoleToggle = (ecoleId) => {
        setFormData(prev => {
            const currentEcoles = [...prev.ecoles];
            if (currentEcoles.includes(ecoleId)) {
                return { ...prev, ecoles: currentEcoles.filter(id => id !== ecoleId) };
            } else {
                return { ...prev, ecoles: [...currentEcoles, ecoleId] };
            }
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (formData.ecoles.length === 0) {
            alert("Veuillez sélectionner au moins une école pour cet administrateur.");
            return;
        }

        // The first selected ecole is set as primary 'ecole', the rest in 'ecoles'
        const payload = {
            ...formData,
            ecole: formData.ecoles[0] 
        };

        const res = await handleSend(e, "/user/signin", payload, setLoading, token);
        if (res && res.state === "success") {
            navigate("/super-admin/admins");
        }
    };

    return (
        <div className="flex flex-col gap-6 w-full max-w-3xl mx-auto">
            <div className="flex items-center gap-3 border-b border-gray-200 pb-4 flex-wrap">
                <Link to="/super-admin/admins" className="p-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition text-gray-500 flex-shrink-0">
                    <ArrowLeft className="h-5 w-5" />
                </Link>
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-lg flex-shrink-0">
                    <Shield className="h-6 w-6" />
                </div>
                <div className="min-w-0">
                    <h1 className="text-xl sm:text-2xl font-bold text-gray-900 tracking-tight">Nouvel Administrateur</h1>
                    <p className="text-sm text-gray-500">Créer un compte administrateur multi-écoles.</p>
                </div>
            </div>

            <form onSubmit={handleSubmit} className="bg-white shadow-sm border border-gray-200 rounded-xl overflow-hidden">
                <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Nom *</label>
                        <input required type="text" name="nom" value={formData.nom} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Prénom *</label>
                        <input required type="text" name="prenom" value={formData.prenom} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Email *</label>
                        <input required type="email" name="email" value={formData.email} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Téléphone *</label>
                        <input required type="text" name="tel" value={formData.tel} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Adresse</label>
                        <input type="text" name="adresse" value={formData.adresse} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-emerald-500" />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium text-gray-700">Mot de passe temporaire *</label>
                        <input required type="text" name="password" value={formData.password} onChange={handleChange} className="w-full border border-gray-300 rounded-lg py-2 px-3 focus:ring-2 focus:ring-emerald-500" />
                        <p className="text-xs text-gray-500">Doit contenir 8 car., majuscule, chiffre, car. spécial.</p>
                    </div>

                    <div className="space-y-3 md:col-span-2 pt-4 border-t border-gray-100">
                        <label className="text-sm font-bold text-gray-900 block">Écoles Assignées *</label>
                        <p className="text-xs text-gray-500 mb-2">Sélectionnez une ou plusieurs écoles à gérer.</p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {ecoles.map(ecole => (
                                <label key={ecole._id} className={`flex items-center gap-3 p-3 border rounded-lg cursor-pointer transition-all ${formData.ecoles.includes(ecole._id) ? 'border-emerald-500 bg-emerald-50' : 'border-gray-200 hover:bg-gray-50'}`}>
                                    <input 
                                        type="checkbox" 
                                        className="w-4 h-4 text-emerald-600 rounded border-gray-300 focus:ring-emerald-500"
                                        checked={formData.ecoles.includes(ecole._id)}
                                        onChange={() => handleEcoleToggle(ecole._id)}
                                    />
                                    <div>
                                        <div className="text-sm font-medium text-gray-900">{ecole.nom}</div>
                                        <div className="text-xs text-gray-500">{ecole.ville}</div>
                                    </div>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                <div className="bg-gray-50 px-4 sm:px-6 py-4 border-t border-gray-200 flex flex-col-reverse sm:flex-row justify-end gap-3">
                    <Link to="/super-admin/admins" className="px-4 py-2 text-sm font-medium text-center text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition">
                        Annuler
                    </Link>
                    <SubmitButton type="submit" isSubmitting={loading} disabled={loading} className="inline-flex items-center justify-center gap-2 px-6 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 transition shadow-sm disabled:opacity-50">
                        <Save className="h-4 w-4" />
                        {loading ? "Création..." : "Créer l'Administrateur"}
                    </SubmitButton>
                </div>
            </form>
        </div>
    );
}
